import React, { useEffect, useMemo } from 'react';
import { Upload, LineChart } from 'lucide-react';
import { Card, CardHeader, CardFooter, AddButton, CollapsibleContent, EditModeToggle, EditModeCheckbox, EditModeActionBar } from '../shared';
import { InlineEditField, InlineEditText } from '../shared/InlineEditField';
import {
  useReturnPeriod,
  ReturnCell,
  ReturnPeriodHeader,
  totalReturnPct,
  pickPeriodReturn,
} from '../shared/ReturnPeriodControls';
import { UpgradePrompt } from '../modals/UpgradeModal';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { useInlineEdit } from '../../hooks/useInlineEdit';
import { useFinancialCalculations } from '../../hooks/useFinancialCalculations';
import { useCollapsedWidgets } from '../../hooks/useCollapsedWidgets';
import { useEditMode } from '../../hooks/useEditMode';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { INVESTMENT_ACCOUNT_TYPES } from '../../config/constants';
import { Investment } from '../../types';

const RETURN_PERIOD_STORAGE_KEY = 'tallyup_investment_return_period';

interface InvestmentsWidgetProps {
  onAdd: () => void;
  onUpgradeClick: () => void;
  onImportCSV: () => void;
  variant?: 'card' | 'page';
}

export const InvestmentsWidget: React.FC<InvestmentsWidgetProps> = ({
  onAdd,
  onUpgradeClick,
  onImportCSV,
  variant = 'card',
}) => {
  const { darkMode, privacyMode } = useTheme();
  const { sheetId } = useAuth();
  const { totalInvestmentValue, updateInvestment, handleDeleteInvestment, handleBatchDelete } = useFinancialData();
  const { visibleInvestments, hasHiddenInvestments, hiddenInvestmentCount, isAtInvestmentLimit } = useFinancialCalculations();
  const { editValue, setEditValue, isEditing, startEdit, cancelEdit, completeEdit, completeEditText } =
    useInlineEdit();
  const { isCollapsed, toggleCollapse } = useCollapsedWidgets();
  const collapsed = isCollapsed('investments');

  const handleDeleteAsync = async (index: number) => {
    if (!sheetId) return;
    await handleDeleteInvestment(index, sheetId);
  };
  const handleBatchDeleteAsync = async (indices: number[]) => {
    if (!sheetId) return;
    await handleBatchDelete('Investments', indices, sheetId);
  };

  const editMode = useEditMode(handleDeleteAsync, visibleInvestments.length, handleBatchDeleteAsync);

  const {
    returnPeriod,
    setPeriod,
    periodMenuOpen,
    setPeriodMenuOpen,
    activePeriod,
  } = useReturnPeriod(RETURN_PERIOD_STORAGE_KEY);

  // Exit edit mode when widget collapses
  useEffect(() => {
    if (collapsed) editMode.exitEditMode();
  }, [collapsed, editMode]);

  const handleSaveNumeric = async (index: number, field: 'shares' | 'costBasis') => {
    const { value, isValid } = completeEdit();
    if (isValid && sheetId) {
      await updateInvestment(index, field, value, sheetId);
    }
  };

  const handleSaveText = async (index: number, field: 'symbol') => {
    const { value, isValid } = completeEditText();
    if (isValid && sheetId) {
      await updateInvestment(index, field, value, sheetId);
    } else {
      cancelEdit();
    }
  };

  const handleAddClick = () => {
    if (isAtInvestmentLimit) {
      onUpgradeClick();
    } else {
      onAdd();
    }
  };

  // Group investments by account type
  const { groups, showGroups } = useMemo(() => {
    const accountSet = new Set(visibleInvestments.map(inv => inv.account || 'Individual'));
    const shouldGroup = accountSet.size >= 2;

    if (!shouldGroup) {
      return {
        groups: [{ account: '', investments: visibleInvestments.map((inv, idx) => ({ inv, originalIndex: idx })) }],
        showGroups: false,
      };
    }

    // Build groups in INVESTMENT_ACCOUNT_TYPES order, then any unlisted accounts
    const groupMap = new Map<string, Array<{ inv: Investment; originalIndex: number }>>();
    visibleInvestments.forEach((inv, idx) => {
      const acct = inv.account || 'Individual';
      if (!groupMap.has(acct)) groupMap.set(acct, []);
      groupMap.get(acct)!.push({ inv, originalIndex: idx });
    });

    const ordered: Array<{ account: string; investments: Array<{ inv: Investment; originalIndex: number }> }> = [];
    for (const acctType of INVESTMENT_ACCOUNT_TYPES) {
      const items = groupMap.get(acctType);
      if (items) {
        ordered.push({ account: acctType, investments: items });
        groupMap.delete(acctType);
      }
    }
    // Any remaining accounts not in the predefined list
    Array.from(groupMap.entries()).forEach(([acct, items]) => {
      ordered.push({ account: acct, investments: items });
    });

    return { groups: ordered, showGroups: true };
  }, [visibleInvestments]);

  // Compute symbol summaries for symbols appearing in 2+ accounts
  const symbolSummaries = useMemo(() => {
    const symbolMap = new Map<string, Investment[]>();
    groups.forEach((group) => {
      group.investments.forEach(({ inv }) => {
        const sym = inv.symbol.toUpperCase();
        if (!symbolMap.has(sym)) symbolMap.set(sym, []);
        symbolMap.get(sym)!.push(inv);
      });
    });

    const result: Array<{
      symbol: string;
      totalShares: number;
      avgCostBasis: number;
      totalValue: number;
      currentPrice: number;
      percentage: number | null;
    }> = [];

    symbolMap.forEach((entries, sym) => {
      if (entries.length < 2) return;
      const totalShares = entries.reduce((sum, inv) => sum + inv.shares, 0);
      const totalCost = entries.reduce((sum, inv) => sum + inv.shares * inv.costBasis, 0);
      const avgCostBasis = totalShares > 0 ? totalCost / totalShares : 0;
      const totalValue = entries.reduce((sum, inv) => sum + inv.totalValue, 0);
      const currentPrice = entries[0].currentPrice;
      let percentage: number | null = null;
      if (returnPeriod === 'total') {
        const value = totalValue > 0 ? totalValue : totalShares * currentPrice;
        percentage = totalReturnPct(totalCost, value);
      } else {
        // Same ticker shares one market return — use first available reading
        percentage =
          entries
            .map(inv =>
              pickPeriodReturn(returnPeriod, inv, totalReturnPct(inv.shares * inv.costBasis, inv.totalValue))
            )
            .find(v => v != null) ?? null;
      }
      result.push({ symbol: sym, totalShares, avgCostBasis, totalValue, currentPrice, percentage });
    });

    return result;
  }, [groups, returnPeriod]);

  const colCount = editMode.isEditMode ? 8 : 7;
  const isPage = variant === 'page';

  return (
    <Card variant={variant}>
      <CardHeader
        title="Investment Portfolio"
        icon={<LineChart size={20} />}
        action={
          <div className="flex items-center gap-2">
            <EditModeToggle
              isEditMode={editMode.isEditMode}
              onToggle={editMode.toggleEditMode}
              itemCount={visibleInvestments.length}
            />
            <button
              onClick={onImportCSV}
              className={`p-1.5 rounded-lg transition-all ${
                darkMode
                  ? 'text-slate-400 hover:text-violet-400 hover:bg-violet-500/20'
                  : 'text-slate-500 hover:text-violet-600 hover:bg-violet-100'
              }`}
              title="Import CSV"
            >
              <Upload size={18} />
            </button>
            <AddButton onClick={handleAddClick} />
          </div>
        }
        collapsed={isPage ? false : collapsed}
        onToggleCollapse={isPage ? undefined : () => toggleCollapse('investments')}
        variant={variant}
      />

      <CollapsibleContent collapsed={isPage ? false : collapsed} variant={variant}>
      {editMode.isEditMode && (
        <EditModeActionBar
          selectedCount={editMode.selectedIndices.size}
          totalCount={visibleInvestments.length}
          allSelected={editMode.selectedIndices.size === visibleInvestments.length}
          onSelectAll={() => editMode.selectAll(visibleInvestments.length)}
          onDeselectAll={editMode.deselectAll}
          onDeleteSelected={editMode.deleteSelected}
          isDeleting={editMode.isDeleting}
        />
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm [&_th]:px-2 [&_td]:px-2 md:[&_th]:px-1 md:[&_td]:px-1" style={{ minWidth: 600 }}>
          <thead>
            <tr
              className={`${darkMode ? 'text-slate-400' : 'text-slate-600'} border-b ${
                darkMode ? 'border-white/10' : 'border-slate-200'
              }`}
            >
              {editMode.isEditMode && <th className="w-8 pb-2" />}
              <th className="text-left pb-2 whitespace-nowrap">Symbol</th>
              <th className="text-right pb-2 whitespace-nowrap w-[14%]">Shares</th>
              <th className="text-right pb-2 whitespace-nowrap w-[12%]">Cost</th>
              <th className="text-right pb-2 whitespace-nowrap w-[13%]">Total Cost</th>
              <th className="text-right pb-2 whitespace-nowrap w-[12%]">Current</th>
              <th className="text-right pb-2 whitespace-nowrap w-[13%]">Value</th>
              <ReturnPeriodHeader
                darkMode={darkMode}
                activeShort={activePeriod.short}
                periodMenuOpen={periodMenuOpen}
                returnPeriod={returnPeriod}
                onToggleMenu={() => setPeriodMenuOpen(v => !v)}
                onSelect={setPeriod}
              />
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              const groupTotal = group.investments.reduce((sum, { inv }) => sum + inv.totalValue, 0);
              return (
                <React.Fragment key={group.account || '__flat'}>
                  {showGroups && (
                    <tr className={`${darkMode ? 'bg-white/[0.03]' : 'bg-slate-50/80'}`}>
                      <td
                        colSpan={colCount}
                        className={`py-1.5 px-1 border-l-2 ${darkMode ? 'border-violet-500/40' : 'border-violet-400/50'}`}
                      >
                        <div className="flex justify-between items-center">
                          <span className={`text-sm font-semibold tracking-wide ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {group.account}
                          </span>
                          <span className={`text-sm font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            {formatCurrency(groupTotal, privacyMode)}</span>
                        </div>
                      </td>
                    </tr>
                  )}
                  {group.investments.map(({ inv, originalIndex: idx }) => {
                    const totalCost = inv.shares * inv.costBasis;
                    const value = inv.totalValue > 0 ? inv.totalValue : inv.shares * inv.currentPrice;
                    const percentage = pickPeriodReturn(
                      returnPeriod,
                      inv,
                      totalReturnPct(totalCost, value)
                    );

                    return (
                      <tr
                        key={idx}
                        className={`${darkMode ? 'text-slate-300' : 'text-slate-800'} border-b ${
                          darkMode ? 'border-white/10' : 'border-slate-100'
                        }`}
                      >
                        {editMode.isEditMode && (
                          <td className="py-2 pr-2">
                            <EditModeCheckbox
                              checked={editMode.selectedIndices.has(idx)}
                              onToggle={() => editMode.toggleIndex(idx)}
                            />
                          </td>
                        )}
                        <td className="py-2 font-semibold truncate">
                          <InlineEditText
                            value={inv.symbol}
                            isEditing={isEditing('investment', idx, 'symbol')}
                            editValue={editValue}
                            onEditValueChange={setEditValue}
                            onStartEdit={() => startEdit('investment', idx, 'symbol', inv.symbol)}
                            onSave={() => handleSaveText(idx, 'symbol')}
                            onCancel={cancelEdit}
                            editable={inv.editable}
                            width="w-full"
                          />
                        </td>
                        <td className="text-right">
                          <InlineEditField
                            value={inv.shares}
                            isEditing={isEditing('investment', idx, 'shares')}
                            editValue={editValue}
                            onEditValueChange={setEditValue}
                            onStartEdit={() => startEdit('investment', idx, 'shares', inv.shares)}
                            onSave={() => handleSaveNumeric(idx, 'shares')}
                            onCancel={cancelEdit}
                            editable={inv.editable}
                            variant="neutral"
                            width="w-full"
                            privacyMode={privacyMode}
                            formatFn={(v, p) => formatNumber(v, 2, p)}
                          />
                        </td>
                        <td className="text-right">
                          <InlineEditField
                            value={inv.costBasis}
                            isEditing={isEditing('investment', idx, 'costBasis')}
                            editValue={editValue}
                            onEditValueChange={setEditValue}
                            onStartEdit={() => startEdit('investment', idx, 'costBasis', inv.costBasis)}
                            onSave={() => handleSaveNumeric(idx, 'costBasis')}
                            onCancel={cancelEdit}
                            editable={inv.editable}
                            variant="neutral"
                            width="w-full"
                            privacyMode={privacyMode}
                          />
                        </td>
                        <td className="text-right">
                          {formatCurrency(inv.shares * inv.costBasis, privacyMode)}
                        </td>
                        <td className="text-right">
                          {privacyMode ? '•••' : formatCurrency(inv.currentPrice)}
                        </td>
                        <td className="text-right font-semibold">
                          {formatCurrency(inv.totalValue, privacyMode)}
                        </td>
                        <td className="text-right">
                          <ReturnCell percentage={percentage} privacyMode={privacyMode} />
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {symbolSummaries.length > 0 && (
              <>
                <tr><td colSpan={colCount} className="py-1.5" /></tr>
                {symbolSummaries.map((s) => (
                  <tr key={`summary-${s.symbol}`} className={`${darkMode ? 'bg-blue-500/[0.05]' : 'bg-blue-50/50'} border-l-2 border-blue-500/30`}>
                    {editMode.isEditMode && <td />}
                    <td className={`py-2 pl-1 font-semibold ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      {s.symbol} Total
                    </td>
                    <td className={`text-right ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      {formatNumber(s.totalShares, 2, privacyMode)}
                    </td>
                    <td className={`text-right ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      {formatCurrency(s.avgCostBasis, privacyMode)}
                    </td>
                    <td className={`text-right ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      {formatCurrency(s.totalShares * s.avgCostBasis, privacyMode)}
                    </td>
                    <td className={`text-right ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {privacyMode ? '•••' : formatCurrency(s.currentPrice)}
                    </td>
                    <td className={`text-right font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      {formatCurrency(s.totalValue, privacyMode)}
                    </td>
                    <td className="text-right">
                      <ReturnCell percentage={s.percentage} privacyMode={privacyMode} />
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>

        {hasHiddenInvestments && (
          <div className="mt-4">
            <UpgradePrompt
              count={hiddenInvestmentCount}
              type="investments"
              onClick={onUpgradeClick}
            />
          </div>
        )}
      </div>

      </CollapsibleContent>

      <CardFooter>
        <div className="flex justify-between items-center font-semibold text-sm px-2 md:px-1">
          <span className={`${darkMode ? 'text-white' : 'text-slate-800'} flex-1`}>
            Total Portfolio
          </span>
          <span className={`${darkMode ? 'text-violet-400' : 'text-violet-600'} text-right`}>
            {formatCurrency(totalInvestmentValue, privacyMode)}
          </span>
        </div>
      </CardFooter>
    </Card>
  );
};
